'use client';

import React from 'react';
import Link from 'next/link';

const obsidian = 'rgb(8, 11, 18)';
const graphite = 'rgb(19, 22, 28)';
const steel = 'rgb(31, 34, 39)';
const cobalt = 'rgb(43, 98, 239)';
const silverText = 'oklch(95% .005 265)';
const silverMuted = 'oklch(65% .01 265)';

const quickLinks = [
  { label: 'Home', href: '/' },
  { label: 'Services', href: '/services' },
  { label: 'About', href: '/about' },
  { label: 'Get a Quote', href: '/get-a-quote' },
];

export default function NotFound() {
  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: obsidian }}
    >
      {/* Minimal nav bar */}
      <header
        className="px-6 py-4 flex items-center justify-between border-b"
        style={{ borderColor: steel }}
      >
        <Link href="/" className="flex items-center gap-2 group">
          <span
            className="text-xl font-bold tracking-tight"
            style={{ color: silverText }}
          >
            into<span style={{ color: cobalt }}>23</span>
          </span>
        </Link>
        <Link
          href="/"
          className="text-sm font-medium px-4 py-2 rounded-lg transition-colors duration-200"
          style={{
            backgroundColor: cobalt,
            color: '#fff',
          }}
        >
          Back to Home
        </Link>
      </header>

      {/* Main content */}
      <main className="flex-1 flex flex-col items-center justify-center px-6 py-20 text-center">
        {/* 404 display */}
        <div className="relative mb-8 select-none">
          <span
            className="text-[10rem] sm:text-[14rem] font-black leading-none"
            style={{
              color: 'transparent',
              WebkitTextStroke: `2px ${cobalt}`,
              opacity: 0.18,
            }}
          >
            404
          </span>
          <div
            className="absolute inset-0 flex items-center justify-center"
            aria-hidden="true"
          >
            <span
              className="text-[10rem] sm:text-[14rem] font-black leading-none"
              style={{
                color: 'transparent',
                WebkitTextStroke: `1px ${cobalt}`,
                opacity: 0.06,
                filter: 'blur(8px)',
              }}
            >
              404
            </span>
          </div>
        </div>

        <h1
          className="text-3xl sm:text-4xl font-bold mb-4"
          style={{ color: silverText }}
        >
          Page Not Found
        </h1>
        <p
          className="text-base sm:text-lg max-w-md mb-10 leading-relaxed"
          style={{ color: silverMuted }}
        >
          The page you&apos;re looking for doesn&apos;t exist or has been moved.
          Head back to explore Into23&apos;s AI translation and multilingual data
          services.
        </p>

        {/* Primary CTA */}
        <Link
          href="/"
          className="inline-flex items-center gap-2 px-8 py-3 rounded-lg font-semibold text-white transition-all duration-200 hover:opacity-90 mb-10"
          style={{ backgroundColor: cobalt }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3 12l2-2m0 0l7-7 7 7m-9 5v6m0 0H7m4 0h2"
            />
          </svg>
          Go to Homepage
        </Link>

        {/* Quick links */}
        <div
          className="rounded-xl p-6 w-full max-w-sm"
          style={{ backgroundColor: graphite, border: `1px solid ${steel}` }}
        >
          <p
            className="text-xs font-semibold uppercase tracking-widest mb-4"
            style={{ color: silverMuted }}
          >
            Quick Links
          </p>
          <ul className="space-y-2">
            {quickLinks.map((link) => (
              <li key={link.href}>
                <Link
                  href={link.href}
                  className="flex items-center justify-between w-full px-4 py-2.5 rounded-lg text-sm font-medium transition-colors duration-150 group"
                  style={{ color: silverText }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLAnchorElement).style.backgroundColor = steel;
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLAnchorElement).style.backgroundColor = 'transparent';
                  }}
                >
                  {link.label}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-4 h-4 opacity-40 group-hover:opacity-80 transition-opacity"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </main>

      {/* Footer note */}
      <footer
        className="py-4 text-center text-xs border-t"
        style={{ borderColor: steel, color: silverMuted }}
      >
        © {new Date().getFullYear()} Into23. All rights reserved.
      </footer>
    </div>
  );
}